import { inject } from '@angular/core';
import type { FormArray, FormGroup } from '@angular/forms';
import { FormBuilder, FormControl, Validators } from '@angular/forms';

import {
  hoursPerWeekValidator,
  minMaxRangeValidator,
  NUMBER_WITH_SEPARATORS_PATTERN,
} from '@recruitee/common';
import { CustomFieldFormHelpers } from '@recruitee/custom-fields-logic';
import { CustomField, CustomFieldFormGroup } from '@recruitee/custom-fields-types';
import { FeatureSpecDataHelpers } from '@recruitee/feature-spec-data';
import { FeatureCode } from '@recruitee/feature-spec-types';
import type { Requisition, RequisitionOpening } from '@recruitee/requisitions-types';

import type { AvailableFieldsMap, RequisitionForm, WorkModelControls } from '../requisitions.types';
import { FormBox } from '../requisitions.types';

export enum RequisitionAvailableField {
  Category = 'category',
  DepartmentId = 'departmentId',
  Education = 'education',
  EmploymentType = 'employmentType',
  Experience = 'experience',
  GeneralContextJson = 'generalContextJson',
  HiringBudget = 'hiringBudget',
  HiringBudgetCurrency = 'hiringBudgetCurrency',
  HiringManagerId = 'hiringManagerId',
  Hybrid = 'hybrid',
  LocationIds = 'locationIds',
  MinHours = 'minHoursPerWeek',
  MaxHours = 'maxHoursPerWeek',
  OnSite = 'onSite',
  RecruiterId = 'recruiterId',
  Remote = 'remote',
  Salary = 'salary',
  WorkModel = 'workModel',
}

export class RequisitionFormFactory {
  private readonly fb = inject(FormBuilder);
  private readonly featureSpecDataHelpers = inject(FeatureSpecDataHelpers);
  private readonly customFieldFormHelpers = inject(CustomFieldFormHelpers);
  private hasCustomFieldsFeature = this.featureSpecDataHelpers.selectIsEnabled(
    FeatureCode.CustomFields,
  );

  public buildFull(
    requisition: Requisition,
    availableFields: AvailableFieldsMap,
  ): FormGroup<RequisitionForm> {
    const requisitionFields = Object.keys(requisition);

    return this.fb.group<RequisitionForm>({
      ...(requisitionFields.includes(RequisitionAvailableField.Category) && {
        category: this.getBasicControl(requisition.category),
      }),
      ...(requisitionFields.includes(RequisitionAvailableField.DepartmentId) && {
        departmentId: this.getBasicControl(requisition.departmentId),
      }),
      ...(requisitionFields.includes(RequisitionAvailableField.Education) && {
        education: this.getBasicControl(requisition.education),
      }),
      ...(requisitionFields.includes(RequisitionAvailableField.EmploymentType) && {
        employmentType: this.getBasicControl(requisition.employmentType),
      }),
      ...(requisitionFields.includes(RequisitionAvailableField.Experience) && {
        experience: this.getBasicControl(requisition.experience),
      }),
      ...(requisitionFields.includes(RequisitionAvailableField.GeneralContextJson) && {
        generalContextJson: this.getBasicControl(requisition.generalContextJson),
      }),
      ...(requisitionFields.includes(RequisitionAvailableField.HiringBudget) && {
        hiringBudget: this.getHiringBudgetControl(requisition.hiringBudget),
      }),
      ...(requisitionFields.includes(RequisitionAvailableField.HiringBudgetCurrency) && {
        hiringBudgetCurrency: this.getBasicControl(requisition.hiringBudgetCurrency),
      }),
      ...(requisitionFields.includes(RequisitionAvailableField.HiringManagerId) && {
        hiringManagerId: this.getBasicControl(requisition.hiringManagerId),
      }),
      ...(requisitionFields.includes(RequisitionAvailableField.MinHours) && {
        minHoursPerWeek: this.getMinHoursControl(requisition.minHoursPerWeek),
      }),
      ...(requisitionFields.includes(RequisitionAvailableField.MaxHours) && {
        maxHoursPerWeek: this.getMaxHoursControl(requisition.maxHoursPerWeek),
      }),
      openings: this.getOpeningsControl(requisition.openings, availableFields[FormBox.Openings]),
      ...(requisitionFields.includes(RequisitionAvailableField.RecruiterId) && {
        recruiterId: this.getBasicControl(requisition.recruiterId),
      }),
      ...(requisitionFields.includes(RequisitionAvailableField.Salary) && {
        salary: this.getSalaryControl(requisition.salary),
      }),
      ...(requisitionFields.includes(RequisitionAvailableField.LocationIds) &&
        availableFields[FormBox.Location].locations && {
          locationIds: this.getLocationIdsControl(requisition.locationIds),
        }),
      ...(requisitionFields.includes(RequisitionAvailableField.Remote) &&
        requisitionFields.includes(RequisitionAvailableField.Hybrid) &&
        requisitionFields.includes(RequisitionAvailableField.OnSite) &&
        availableFields[FormBox.WorkModel].work_model && {
          workModel: this.getWorkModelControl(requisition),
        }),
      ...(this.hasCustomFieldsFeature() && {
        customFields: this.getCustomFieldsFormArray(requisition?.dynamicFields || []),
      }),
    });
  }

  public buildOpening({
    availableOpeningsFields,
    isSaved,
    opening,
  }: {
    availableOpeningsFields: AvailableFieldsMap[FormBox.Openings];
    isSaved: boolean;
    opening?: RequisitionOpening;
  }): FormGroup {
    const isReasonRequired = availableOpeningsFields['reason'];
    const isAnticipatedStartDateRequired = availableOpeningsFields['anticipated_start_date'];
    const isExplanationRequired = availableOpeningsFields['explanation'];

    return this.fb.group({
      id: this.fb.control(opening?.id || null),
      reason: this.fb.control(
        opening?.reason || null,
        isReasonRequired ? Validators.required : null,
      ),
      anticipatedStartDate: this.fb.control(
        opening?.anticipatedStartDate || null,
        isAnticipatedStartDateRequired ? Validators.required : null,
      ),
      explanation: this.fb.control(
        opening?.explanation || null,
        isExplanationRequired ? Validators.required : null,
      ),
      status: this.fb.control(opening?.status || null),
      saved: this.fb.control(isSaved),
    });
  }

  public buildControl(name: string, requisition: Requisition) {
    if (name === RequisitionAvailableField.Category)
      return this.getBasicControl(requisition.category);
    if (name === RequisitionAvailableField.DepartmentId)
      return this.getBasicControl(requisition.departmentId);
    if (name === RequisitionAvailableField.EmploymentType)
      return this.getBasicControl(requisition.employmentType);
    if (name === RequisitionAvailableField.Experience)
      return this.getBasicControl(requisition.experience);
    if (name === RequisitionAvailableField.GeneralContextJson)
      return this.getBasicControl(requisition.generalContextJson);
    if (name === RequisitionAvailableField.HiringBudget)
      return this.getHiringBudgetControl(requisition.hiringBudget);
    if (name === RequisitionAvailableField.HiringBudgetCurrency)
      return this.getBasicControl(requisition.hiringBudgetCurrency);
    if (name === RequisitionAvailableField.HiringManagerId)
      return this.getBasicControl(requisition.hiringManagerId);
    if (name === RequisitionAvailableField.MinHours)
      return this.getMinHoursControl(requisition.minHoursPerWeek);
    if (name === RequisitionAvailableField.MaxHours)
      return this.getMaxHoursControl(requisition.maxHoursPerWeek);
    if (name === RequisitionAvailableField.RecruiterId)
      return this.getBasicControl(requisition.recruiterId);
    if (name === RequisitionAvailableField.LocationIds)
      return this.getLocationIdsControl(requisition.locationIds);
    if (name === RequisitionAvailableField.WorkModel) return this.getWorkModelControl(requisition);
    if (name === RequisitionAvailableField.Salary) return this.getSalaryControl(requisition.salary);
    return this.getBasicControl(null);
  }

  private getBasicControl<Type>(value: Type): FormControl<Type | null> {
    return this.fb.control(value);
  }

  private getHiringBudgetControl(
    hiringBudget: Requisition[RequisitionAvailableField.HiringBudget],
  ) {
    return this.fb.control(hiringBudget, Validators.pattern(NUMBER_WITH_SEPARATORS_PATTERN));
  }

  private getMinHoursControl(minHoursPerWeek: Requisition[RequisitionAvailableField.MinHours]) {
    return this.fb.control(minHoursPerWeek, [
      hoursPerWeekValidator,
      minMaxRangeValidator('minHoursPerWeek', 'maxHoursPerWeek'),
    ]);
  }

  private getMaxHoursControl(maxHoursPerWeek: Requisition[RequisitionAvailableField.MaxHours]) {
    return this.fb.control(maxHoursPerWeek, [
      hoursPerWeekValidator,
      minMaxRangeValidator('minHoursPerWeek', 'maxHoursPerWeek'),
    ]);
  }

  private getOpeningsControl(
    openings: Requisition['openings'],
    availableOpeningsFields: AvailableFieldsMap[FormBox.Openings],
  ) {
    return this.fb.array(
      openings.map(opening =>
        this.buildOpening({ availableOpeningsFields, isSaved: true, opening }),
      ),
    );
  }

  private getSalaryControl(salary: Requisition[RequisitionAvailableField.Salary]) {
    return this.fb.group({
      min: [
        salary.min,
        [Validators.pattern(NUMBER_WITH_SEPARATORS_PATTERN), minMaxRangeValidator('min', 'max')],
      ],
      max: [
        salary.max,
        [Validators.pattern(NUMBER_WITH_SEPARATORS_PATTERN), minMaxRangeValidator('min', 'max')],
      ],
      period: this.fb.control(salary.period),
      currency: this.fb.control(salary.currency),
    });
  }

  private getLocationIdsControl(locationIds: number[] = []): FormArray<FormControl> {
    const controls = locationIds.map(
      locationId => new FormControl(locationId, { nonNullable: true }),
    );
    return this.fb.array(controls);
  }

  private getWorkModelControl(requisition: Requisition): FormGroup<WorkModelControls> {
    return this.fb.group({
      hybrid: this.fb.control(requisition.hybrid, { nonNullable: true }),
      onSite: this.fb.control(requisition.onSite, { nonNullable: true }),
      remote: this.fb.control(requisition.remote, { nonNullable: true }),
    });
  }

  private getCustomFieldsFormArray(customFields: CustomField[]): FormArray<CustomFieldFormGroup> {
    const sortedCustomFields = [...customFields].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );

    const formGroups = sortedCustomFields.map(customField =>
      this.customFieldFormHelpers.getCustomFieldFormGroup(customField),
    );
    return this.fb.array(formGroups);
  }
}
